import React, { FC, useEffect, useState } from "react";

import { Attachment, IAttachment } from "@/data/attachments";
import { IConsultantProfileDto } from "@/data/consultant-profiles";
import { Flex, IDialogModal, inDialogModal, TStyleSize } from "@/eworkitComponents";
import { CloseButton } from "@/views/components/CloseButton";
import { CompletePrivateProfile, PrivatePersonUploadCv } from "@/views/components/ConsultantProfile/PrivatePerson";
import { IProfileSuggestedDataFormValues } from "@/views/components/ConsultantProfile/SuggestedData";
import { StyledModal } from "@/views/components/Modal";

import { TCreatePrivatePersonStep } from "./types";

import "./_create-private-person-dialog-modal.scss";

interface ICreatePrivatePersonDialogProps extends IDialogModal {
  onSizeChange: (size: TStyleSize) => void;
}

const CreatePrivatePersonDialog: FC<ICreatePrivatePersonDialogProps> = ({ onClose, onSizeChange }) => {
  const [step, setStep] = useState<TCreatePrivatePersonStep>("upload-cv");
  const [profile, setProfile] = useState<IConsultantProfileDto>({
    cvs: [],
    skills: [],
    languages: [],
    roles: [],
  });

  const setCvs = (cvs: IAttachment[]) => {
    setProfile(prevProfile => ({ ...prevProfile, cvs }));
  };

  useEffect(() => {
    if (step === "upload-cv") {
      onSizeChange("xs");
    } else if (step === "complete-profile") {
      onSizeChange("sm");
    }
  }, [step, onSizeChange]);

  const handleCvParsed = (cv?: Attachment) => {
    setCvs(cv ? [cv] : []);
    setStep("complete-profile");
  };

  const handleCompleteProfileCancel = () => {
    setStep("upload-cv");
  };

  const handleCompleteProfileValuesChange = (values: IProfileSuggestedDataFormValues) => {
    setProfile(prevProfile => ({ ...prevProfile, ...values }));
  };

  const handleProfileCreated = () => {
    onClose();
  };

  const baseClass = "create-private-person-dialog";

  const handleSkipCV = () => {
    setCvs([]);
    setStep("complete-profile");
  };

  return (
    <StyledModal className={baseClass}>
      <CloseButton onClick={onClose} className={`${baseClass}__close-button`} />
      <>
        {step === "upload-cv" && (
          <Flex padding={["xl"]} align="center" justify="center">
            <PrivatePersonUploadCv
              profile={profile}
              onSkipCV={handleSkipCV}
              onCancel={onClose}
              onCvParsed={handleCvParsed}
              hideBackButton
            />
          </Flex>
        )}

        {step === "complete-profile" && (
          <Flex padding={["xl"]} align="center" justify="center">
            <CompletePrivateProfile
              profile={profile}
              onCancel={handleCompleteProfileCancel}
              isCvUploaded={profile.cvs && profile.cvs.length > 0}
              onValuesChange={handleCompleteProfileValuesChange}
              onProfileCreated={handleProfileCreated}
            />
          </Flex>
        )}
      </>
    </StyledModal>
  );
};

const CreatePrivatePersonDialogModalComponent = inDialogModal(CreatePrivatePersonDialog);

interface ICreatePrivatePersonDialogModalProps extends Omit<ICreatePrivatePersonDialogProps, "onSizeChange"> {}

export const CreatePrivatePersonDialogModal: FC<ICreatePrivatePersonDialogModalProps> = props => {
  const [size, setSize] = useState<TStyleSize>("sm");

  const handleSizeChange = (newSize: TStyleSize) => {
    setSize(newSize);
  };

  return <CreatePrivatePersonDialogModalComponent size={size} onSizeChange={handleSizeChange} {...props} />;
};
